import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { useOrganization } from "../organization/OrganizationProvider";
import "./InventoryDetailPage.css";

type InventoryItem = {
  id: string;
  organization_id: string;
  item_type: string;
  sku: string | null;
  name: string;
  description: string | null;
  unit: string;
  sale_price: number | string;
  cost_price: number | string;
  tax_rate: number | string;
  track_inventory: boolean;
  status: string;
  updated_at: string;
};

type InventorySummary = {
  quantity: number | string;
  reserved_quantity: number | string;
  available_quantity: number | string;
  stock_value: number | string;
  warehouse_count: number | string;
  movement_count: number | string;
};

type InventoryPosition = {
  warehouse_id: string;
  warehouse_code: string;
  warehouse_name: string;
  warehouse_address: string | null;
  quantity: number | string;
  reserved_quantity: number | string;
  available_quantity: number | string;
  minimum_quantity: number | string;
  average_cost: number | string;
  stock_value: number | string;
  low_stock: boolean;
  updated_at: string | null;
};

type InventoryMovement = {
  id: string;
  warehouse_id: string;
  warehouse_code: string;
  warehouse_name: string;
  movement_type: string;
  quantity: number | string;
  unit_cost: number | string;
  movement_value: number | string;
  reference_type: string | null;
  reference_id: string | null;
  reference_number: string | null;
  notes: string | null;
  created_by: string | null;
  created_at: string;
};

type InventoryDetail = {
  item: InventoryItem;
  summary: InventorySummary;
  positions: InventoryPosition[];
  movements: InventoryMovement[];
};

const MOVEMENT_LABELS: Record<string, string> = {
  opening: "Stock inicial",
  purchase: "Compra",
  sale: "Venta",
  service_use: "Uso en taller",
  return_in: "Devolución de entrada",
  return_out: "Devolución de salida",
  adjustment_in: "Ajuste de entrada",
  adjustment_out: "Ajuste de salida",
};

const OUT_TYPES = new Set([
  "sale",
  "service_use",
  "return_out",
  "adjustment_out",
]);

function num(value: number | string | null) {
  return Number(value || 0);
}

function money(value: number | string | null) {
  return new Intl.NumberFormat("es-PE", {
    style: "currency",
    currency: "PEN",
  }).format(num(value));
}

function movementReferencePath(movement: InventoryMovement) {
  if (!movement.reference_id) return null;

  switch (movement.reference_type) {
    case "sale":
      return `/ventas/${movement.reference_id}`;
    case "service_order":
      return `/taller/${movement.reference_id}`;
    case "purchase_receipt":
      return "/compras";
    default:
      return null;
  }
}

export function InventoryDetailPage() {
  const { catalogItemId } = useParams();
  const { selectedOrganization } = useOrganization();

  const [detail, setDetail] = useState<InventoryDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!catalogItemId || !selectedOrganization) return;

    setLoading(true);
    setError(null);

    const { data, error: rpcError } = await supabase.rpc(
      "get_inventory_item_detail",
      {
        p_catalog_item_id: catalogItemId,
      },
    );

    setLoading(false);

    if (rpcError) {
      setDetail(null);
      setError(rpcError.message);
      return;
    }

    const nextDetail = data as InventoryDetail;

    if (
      nextDetail.item.organization_id !== selectedOrganization.organizationId
    ) {
      setDetail(null);
      setError("El producto pertenece a otra empresa.");
      return;
    }

    setDetail(nextDetail);
  }, [catalogItemId, selectedOrganization]);

  useEffect(() => {
    setDetail(null);
    setError(null);
    void load();
  }, [load]);

  if (!selectedOrganization) {
    return (
      <div className="page">
        <section className="empty-state">
          <strong>Selecciona una empresa.</strong>
        </section>
      </div>
    );
  }

  if (loading && !detail) {
    return (
      <div className="page">
        <section className="empty-state">
          <strong>Cargando inventario…</strong>
        </section>
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="page">
        <div className="inline-alert">{error ?? "Producto no disponible."}</div>
        <Link className="compact-button inventory-detail-back" to="/inventario">
          Volver a inventario
        </Link>
      </div>
    );
  }

  const { item, summary, positions, movements } = detail;

  return (
    <div className="page">
      <header className="page-header page-header-actions">
        <div>
          <span className="eyebrow">Inventario</span>
          <h1>{item.name}</h1>
          <p>
            {item.sku ?? "Sin SKU"} · {item.unit} · {item.item_type}
          </p>
        </div>

        <Link className="ghost-button inventory-detail-back" to="/inventario">
          Volver
        </Link>
      </header>

      {error ? <div className="inline-alert">{error}</div> : null}

      <section className="inventory-detail-summary">
        <div className="metric-card">
          <span>Existencia</span>
          <strong>{num(summary.quantity).toFixed(3)}</strong>
        </div>
        <div className="metric-card">
          <span>Disponible</span>
          <strong>{num(summary.available_quantity).toFixed(3)}</strong>
        </div>
        <div className="metric-card">
          <span>Valor de stock</span>
          <strong>{money(summary.stock_value)}</strong>
        </div>
        <div className="metric-card">
          <span>Movimientos</span>
          <strong>{Number(summary.movement_count)}</strong>
        </div>
      </section>

      <section className="inventory-detail-grid">
        <article className="panel-card">
          <div className="section-heading">
            <div>
              <span className="eyebrow">Producto</span>
              <h2>Datos de catálogo</h2>
            </div>
          </div>

          <dl className="inventory-detail-meta">
            <div>
              <dt>SKU</dt>
              <dd>{item.sku ?? "—"}</dd>
            </div>
            <div>
              <dt>Estado</dt>
              <dd>{item.status}</dd>
            </div>
            <div>
              <dt>Costo catálogo</dt>
              <dd>{money(item.cost_price)}</dd>
            </div>
            <div>
              <dt>Precio de venta</dt>
              <dd>{money(item.sale_price)}</dd>
            </div>
            <div>
              <dt>IGV</dt>
              <dd>{num(item.tax_rate)}%</dd>
            </div>
            <div>
              <dt>Almacenes</dt>
              <dd>{Number(summary.warehouse_count)}</dd>
            </div>
          </dl>

          {item.description ? (
            <p className="inventory-detail-description">{item.description}</p>
          ) : null}
        </article>

        <article className="panel-card">
          <div className="section-heading">
            <div>
              <span className="eyebrow">Reserva</span>
              <h2>Disponibilidad</h2>
            </div>
          </div>

          <dl className="inventory-detail-meta">
            <div>
              <dt>Existencia</dt>
              <dd>{num(summary.quantity).toFixed(3)}</dd>
            </div>
            <div>
              <dt>Reservado</dt>
              <dd>{num(summary.reserved_quantity).toFixed(3)}</dd>
            </div>
            <div>
              <dt>Disponible</dt>
              <dd>{num(summary.available_quantity).toFixed(3)}</dd>
            </div>
            <div>
              <dt>Valor</dt>
              <dd>{money(summary.stock_value)}</dd>
            </div>
          </dl>
        </article>
      </section>

      <section className="panel-card">
        <div className="section-heading">
          <div>
            <span className="eyebrow">Almacenes</span>
            <h2>Posiciones de stock</h2>
          </div>
        </div>

        <div className="inventory-position-grid">
          {positions.map((position) => (
            <article
              className={
                position.low_stock
                  ? "inventory-position-card inventory-position-low"
                  : "inventory-position-card"
              }
              key={position.warehouse_id}
            >
              <div className="inventory-position-heading">
                <div>
                  <span
                    className={
                      position.low_stock
                        ? "status-badge status-badge-expired"
                        : "status-badge"
                    }
                  >
                    {position.low_stock ? "Stock bajo" : position.warehouse_code}
                  </span>
                  <h3>{position.warehouse_name}</h3>
                  <p>{position.warehouse_address ?? "Sin dirección"}</p>
                </div>
                <strong>{num(position.available_quantity).toFixed(3)}</strong>
              </div>

              <dl className="inventory-detail-meta">
                <div>
                  <dt>Existencia</dt>
                  <dd>{num(position.quantity).toFixed(3)}</dd>
                </div>
                <div>
                  <dt>Reservado</dt>
                  <dd>{num(position.reserved_quantity).toFixed(3)}</dd>
                </div>
                <div>
                  <dt>Mínimo</dt>
                  <dd>{num(position.minimum_quantity).toFixed(3)}</dd>
                </div>
                <div>
                  <dt>Costo promedio</dt>
                  <dd>{money(position.average_cost)}</dd>
                </div>
                <div>
                  <dt>Valor</dt>
                  <dd>{money(position.stock_value)}</dd>
                </div>
              </dl>
            </article>
          ))}
        </div>
      </section>

      <section className="panel-card">
        <div className="section-heading">
          <div>
            <span className="eyebrow">Trazabilidad</span>
            <h2>Movimientos recientes</h2>
          </div>
          <span className="status-badge">
            {movements.length} mostrados
          </span>
        </div>

        {movements.length === 0 ? (
          <p className="settings-empty">No hay movimientos registrados.</p>
        ) : (
          <div className="inventory-movement-list">
            {movements.map((movement) => {
              const outgoing = OUT_TYPES.has(movement.movement_type);
              const referencePath = movementReferencePath(movement);

              return (
                <article className="inventory-movement-row" key={movement.id}>
                  <div className="inventory-movement-main">
                    <div>
                      <span
                        className={
                          outgoing
                            ? "inventory-movement-sign inventory-movement-out"
                            : "inventory-movement-sign inventory-movement-in"
                        }
                      >
                        {outgoing ? "−" : "+"}
                        {num(movement.quantity).toFixed(3)}
                      </span>
                      <strong>
                        {MOVEMENT_LABELS[movement.movement_type] ??
                          movement.movement_type}
                      </strong>
                    </div>
                    <span>
                      {movement.warehouse_code} · {movement.warehouse_name}
                    </span>
                    {movement.notes ? <small>{movement.notes}</small> : null}
                  </div>

                  <dl className="inventory-movement-meta">
                    <div>
                      <dt>Costo unitario</dt>
                      <dd>{money(movement.unit_cost)}</dd>
                    </div>
                    <div>
                      <dt>Valor</dt>
                      <dd>{money(movement.movement_value)}</dd>
                    </div>
                    <div>
                      <dt>Referencia</dt>
                      <dd>
                        {referencePath ? (
                          <Link
                            className="inventory-reference-link"
                            to={referencePath}
                          >
                            {movement.reference_number ??
                              movement.reference_type ??
                              "Abrir"}
                          </Link>
                        ) : (
                          movement.reference_number ??
                          movement.reference_type ??
                          "Manual"
                        )}
                      </dd>
                    </div>
                    <div>
                      <dt>Fecha</dt>
                      <dd>
                        {new Date(movement.created_at).toLocaleString("es-PE")}
                      </dd>
                    </div>
                  </dl>
                </article>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
